<script>
import Theme from '@/theme'
import StringUtils from '@/utils/stringUtils'
import EventsUtils from '@/utils/eventsUtils'

const REF_FIELD_WRAPPER = 'field-wrapper'
const REF_INPUT = 'input'
const REF_LEFT_BORDER = 'left-border'
const REF_RIGHT_BORDER = 'right-border'
const REF_BOTTOM_BORDER = 'bottom-border'
const REF_PLACEHOLDER = ''
const REF_INPUT_HINT_MESSAGE = 'input-hint-message'

export default
{
    name: 'floating-label-text-input',

    props:
    {
        placeholder: { type: String, default: '' },
        transitionDuration: { type: String, default: '150ms' },
        type: { type: String, default: 'text' },
        icon: { type: String, default: '' },
        hintMessage: { type: String, default: '' },
        autocapitalize: { type: String, default: '' },
        
        // bound to v-model.
        value: { type: String, default: '' }
    },

    created ()
    {
        this.inputText = this.value
        window.addEventListener('resize', this.onWindowResize)
    },

    render () 
    {
        return (
            <div class='floating-label-text-input'>
                <div class='field-wrapper' ref={REF_FIELD_WRAPPER}>
                    <form onsubmit='return false'>
                        <input ref={REF_INPUT} style={{ width: this.inputWidth }} autocomplete='off' autofill='off' autocapitalize={this.autocapitalize}
                            on-keydown={this.onKeyDown} value={this.inputText} on-input={this.onInput} on-focus={this.onInputFocus} on-blur={this.onInputBlur} />
                    </form>
                    <div class='border no-right' ref={REF_LEFT_BORDER} />
                    <div class='field-placeholder' ref={REF_PLACEHOLDER} style={{ transitionDuration: this.transitionDuration }} >
                        <span>{ this.placeholder }</span>
                    </div>
                    <inline-svg src={this.icon} class='field-icon' />
                    <div class='border no-left' ref={REF_RIGHT_BORDER} />
                    <div class='border bottom' ref={REF_BOTTOM_BORDER} />
                </div>
                <div ref={REF_INPUT_HINT_MESSAGE} class='input-hint-message'>{ this.inputHintMessage }</div>
                <div style={{ display: 'inline-block' }} />
            </div>
        )
    },

    mounted ()
    {
        this.hideHintMessage()
        this.input.type = this.type
        this.setHintMessage(this.hintMessage)
        this.onWindowResize()
    },

    beforeDestroy ()
    {
        window.removeEventListener('resize', this.onWindowResize)
    },

    data () 
    {
        return {
            inputHintMessage: '',
            marginBottom: null,
            activeColor: Theme.properties.mainColor,
            inactiveColor: 'inherit',
            errorColor: 'red',
            inputText: '',
            lastResizeEventTimestamp: 0,
        }
    },

    watch:
    {
        value: function (val, oldVal)
        {
            this.inputText = val
        },

        inputText: function (val, oldVal)
        {
            this.hideHintMessage()

            val = val.toString()
            oldVal = oldVal.toString()

            const isValEmpty = StringUtils.isEmptyString(val)
            const isOldValEmpty = StringUtils.isEmptyString(oldVal)

            if (isValEmpty == isOldValEmpty) return

            if (isValEmpty)
            {
                // ensure that text has been empty for longer than 50ms:
                // when keyboard word suggestion is selected, text is emptied & replaced.
                setTimeout(() =>
                {
                    if (!StringUtils.isEmptyString(this.inputText)) return

                    this.onInputEmpty()
                }, 50)
            }

            else this.onInputHasText()
        }
    },

    computed:
    {
        inputWidth ()
        {
            return StringUtils.isEmptyString(this.icon) ? '90%' : '75%'
        },

        fieldWrapper ()
        {
            return this.$refs[REF_FIELD_WRAPPER]
        },

        input ()
        {
            return this.$refs[REF_INPUT]
        },

        placeholderElem ()
        {
            return this.$refs[REF_PLACEHOLDER]
        },

        leftBorder ()
        {
            return this.$refs[REF_LEFT_BORDER]
        },

        rightBorder ()
        {
            return this.$refs[REF_RIGHT_BORDER]
        },

        bottomBorder ()
        {
            return this.$refs[REF_BOTTOM_BORDER]
        },

        inputHint ()
        {
            return this.$refs[REF_INPUT_HINT_MESSAGE]
        }
    },

    methods:
    {
        isInputFocused ()
        {
            return window.document.activeElement == this.input
        },

        getInputFontSize ()
        {
            return window.getComputedStyle(this.input).fontSize
        },

        blur ()
        {
            this.input.blur()
        },

        setBordersColor (color)
        {
            this.leftBorder.style.borderColor = this.rightBorder.style.borderColor = this.bottomBorder.style.borderColor = color
        },

        setActiveStyle ()
        {
            this.setBordersColor(this.activeColor)
        },

        setInactiveStyle ()
        {
            this.setBordersColor(this.inactiveColor)
        },

        setErrorStyle ()
        {
            this.setBordersColor(this.errorColor)
            this.inputHint.style.color = this.errorColor
        },

        showHintMessage ()
        {
            const isHintMessageAlreadyShown = this.inputHint.style.display != 'none'
            if (isHintMessageAlreadyShown) return

            this.inputHint.style.display = 'inherit'
            this.marginBottom = this.$el.style.marginBottom

            this.$nextTick(() =>
            {
                this.$el.style.marginBottom = this.inputHint.clientHeight + 'px'
            })
        },

        hideHintMessage ()
        {
            this.inputHint.style.display = 'none'
            this.$el.style.marginBottom = this.marginBottom
        },

        setHintMessage (value)
        {
            this.inputHintMessage = value

            if (StringUtils.isEmptyString(this.inputHintMessage)) this.hideHintMessage()
            else this.showHintMessage()
        },

        setErrorMessage (message)
        {
            this.setHintMessage(message)
            this.setErrorStyle()
        },

        onWindowResize (event)
        {
            this.onInputEmpty(event)
            this.onInputBlur(event)
            
            if (this.isInputFocused()) this.onInputFocus(event)
            if (!StringUtils.isEmptyString(this.inputText))
            {
                this.onInputHasText()
            }
        },

        onKeyDown (event)
        {
            if (event.keyCode == 13) this.$emit('enter')
        },

        onInput (event)
        {
            this.inputText = event.target.value

            this.$emit('input', this.inputText)
        },

        onInputFocus (event)
        {
            this.$emit('focus')

            this.setActiveStyle()

            this.hideHintMessage()
        },

        onInputBlur (event)
        {
            this.$emit('blur')
            
            this.setInactiveStyle()
        },

        onInputHasText ()
        {
            if (StringUtils.isEmptyString(this.placeholder)) return
            
            const scaleRatio = 0.75
            const placeholderTopExtraMargin = -6
            const translatedYPos = `calc(-${this.getInputFontSize()} / 2 + ${placeholderTopExtraMargin}px)`;
            this.placeholderElem.style.transform = `scale(${scaleRatio}) translateY(${translatedYPos})`
            this.placeholderElem.style.top = '0px'

            const transitionDuration = Number.parseInt(this.transitionDuration)
            
            EventsUtils.setDelayedInterval(() =>
            {
                const placeholderRect = this.placeholderElem.getBoundingClientRect()
                const placeholderRelativePositionX = placeholderRect.x - this.fieldWrapper.getBoundingClientRect().x
                const placeholderRelativePositionEndX = placeholderRelativePositionX + placeholderRect.width
                const placeholderMarginRight = 5

                this.leftBorder.style.width = placeholderRelativePositionX + 'px'
                this.rightBorder.style.width = `calc(100% - ${placeholderRelativePositionEndX}px - ${placeholderMarginRight}px)`
            }, transitionDuration / 2, transitionDuration * (1/3), transitionDuration * 2 + 100);
        },

        onInputEmpty ()
        {
            const extraMarginTop = 2
            var placeholderHeight
            if (this.placeholderElem.offsetHeight)
            {
                placeholderHeight = this.placeholderElem.offsetHeight + 'px'
            }
            else return setTimeout(this.onInputEmpty, 100)

            this.placeholderElem.style.transform = 'scale(1) translateY(0)'
            this.placeholderElem.style.top = `calc(50% - ${placeholderHeight} / 2 + ${extraMarginTop}px)`

            this.leftBorder.style.width = this.rightBorder.style.width = '50%'
        }
    },

    components:
    {
    }
}
</script>

<style lang='less'>
@import '~@/styles/main.less';

@placeholder-padding-side: 10px;
@border-size: 2px;

.floating-label-text-input
{
    height: 12vw;
    font-size: 4vw;
    border-color: var(--theme-border-color);
    color: var(--theme-text-color);

    .OnTablet({
        height: 5vh;
        font-size: 2.2vw;
    });

    .OnDesktop({
        height: 5vh;
        font-size: 1.7vh;
    });

    .field-wrapper
    {
        position: relative;
        margin-bottom: @border-size * 2;
        display: flex;
        flex-direction: row;
        margin-bottom: -15px;
        height: inherit;
        border-color: inherit;
        color: inherit;

        input
        {
            position: absolute;
            background-color: transparent;
            margin-left: @placeholder-padding-side * 2;
            margin-right: @placeholder-padding-side * 2;
            height: 100%;  
            margin-top: auto;
            margin-bottom: auto;
            top: @border-size;
            font-size: inherit;
            color: inherit;
        }

        .field-placeholder
        {
            pointer-events: none;
            box-sizing: border-box;
            padding: 0 8px;
            position: absolute;
            transition: transform cubic-bezier(0.4,0,0.2,1), opacity cubic-bezier(0.4,0,0.2,1);
            z-index: 1;
            text-align: left;
            color: #ADB4BF;
            user-select: none;

            span
            {
                padding-left: @placeholder-padding-side;
            }
        }

        .border
        {
            border: @border-size solid;
            border-color: inherit;
            border-radius: 4px;
            position: absolute;
            height: 100%;
            pointer-events: none;
            transition: border-color .10s ease-out;

            &.no-right
            {
                border-top-right-radius: 0; 
                border-bottom-right-radius: 0; 
                border-right: none;
                left: 0;
            }

            &.no-left
            {
                border-top-left-radius: 0; 
                border-bottom-left-radius: 0; 
                border-left: none;
                right: 0;
            }

            &.bottom
            {
                border-top: none;
                border-left: none;
                border-right: none;
                border-radius: 0;
                left: 1%;
                width: 98%;
                bottom: -@border-size * 2;
            }
        }

        .field-icon
        {
            position: absolute;
            height: 50%;
            top: calc(25% + @border-size);
            right: @border-size * 6;
            fill: #ADB4BF;
        }
    }

    .input-hint-message
    {
        font-size: 2vh;
        padding-top: @placeholder-padding-side * 2 + @border-size * 2;
        margin-bottom: -@placeholder-padding-side * 2;
        margin-left: 5px;
        display: inline-block;

        .OnLargeMobile({ font-size: 1.7vh; });
        .OnTablet({ font-size: 1.5vh; });
    }
}
</style>


// WEBPACK FOOTER //
// src/components/FloatingLabelTextInput.vue